import { useEffect, useRef, useState, useCallback } from "react";
import { AddOutlined, CloseOutlined, DeleteOutlineOutlined, EditOutlined, SaveOutlined, SearchOutlined } from "@mui/icons-material";
import { Switch } from "@mui/material";
import { createAdminUser, deleteAdminUser, getAdminUsers, updateAdminUser } from "../../../../services/Dashboard/Table";
import { getPlanErrorMessage } from "./PlansModule";
import { formatDateTime, formatDateOnly } from "../../../../Utils/formatDateTime";

const EMPTY_USER_FORM = {
  first_name: "", last_name: "", email: "", password: "",
  user_verified: true, is_active: true, is_staff: false, is_superuser: false,
};

const sortUsersByDate = (users = []) =>
  [...users].sort((a, b) => {
    const aDate = new Date(a.date_joined || a.created_at || 0).getTime();
    const bDate = new Date(b.date_joined || b.created_at || 0).getTime();
    return bDate - aDate;
  });

function UserStatusPill({ active, isDelete, userVerified }) {
  if (isDelete) {
    return (
      <span className="inline-flex items-center justify-center w-fit min-h-7 md:px-2.5 md:py-2 px-2 py-1.5 rounded-full border md:text-xs text-[10px] font-extrabold uppercase tracking-[0.04em] border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)]">
        Deleted
      </span>
    );
  }

  // If user signed up but has not completed OTP verification / never logged in
  if (userVerified === false) {
    return (
      <span className="inline-flex items-center justify-center w-fit min-h-7 md:px-2.5 md:py-2 px-2 py-1.5 rounded-full border md:text-xs text-[10px] font-extrabold uppercase tracking-[0.04em] border-[rgba(255,184,0,0.35)] bg-[rgba(255,184,0,0.12)] text-[#FFB800]">
        Pending Verification
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center justify-center w-fit min-h-7 md:px-2.5 md:py-2 px-2 py-1.5 rounded-full border md:text-xs text-[10px] font-extrabold uppercase tracking-[0.04em] ${active ? "border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)]" : "border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)]"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

const switchSx = { "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--color-primary)" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--color-primary)" } };

function UserForm({ form, setForm, editingUser, saving, onSubmit, onCancel, formRef, firstInputRef }) {
  const fieldClass = "w-full px-4 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none";
  const labelClass = "grid gap-2 text-[var(--color-text-muted)] md:text-sm text-xs font-bold uppercase tracking-[0.04em]";
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  return (
    <form ref={formRef} onSubmit={onSubmit} className="grid gap-4 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <p className="md:text-sm text-xs label-caps block m-0">{editingUser ? "Edit User" : "New User"}</p>
          <h4 className="xl:text-xl md:text-lg text-base text-[var(--color-text)]">{editingUser ? `${editingUser.first_name} ${editingUser.last_name}` : "Create user account"}</h4>
        </div>
        {editingUser && (
          <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 min-h-[38px] px-3 py-2.5 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text-muted)] cursor-pointer md:text-sm text-xs font-bold">
            <CloseOutlined sx={{ fontSize: 17 }} />Cancel
          </button>
        )}
      </div>
      <div className="admin-user-form-grid grid md:grid-cols-2 grid-cols-1 gap-3">
        <label className={labelClass}>First Name<input ref={firstInputRef} value={form.first_name} onChange={(event) => updateField("first_name", event.target.value)} required className={fieldClass} /></label>
        <label className={labelClass}>Last Name<input value={form.last_name} onChange={(event) => updateField("last_name", event.target.value)} required className={fieldClass} /></label>
        <label className={labelClass}>Email<input type="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} required disabled={!!editingUser} className={`${fieldClass} ${editingUser ? "cursor-not-allowed" : ""}`} /></label>
        {!editingUser && (
          <label className={labelClass}>Password<input type="password" value={form.password} onChange={(event) => updateField("password", event.target.value)} required className={fieldClass} /></label>
        )}
      </div>
      <div className="flex gap-4 flex-wrap">
        <label className={`inline-flex items-center gap-2 text-[var(--color-text)] xl:text-base md:text-sm text-xs font-bold ${(editingUser?.is_delete || editingUser?.user_verified === false) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
          <Switch checked={Boolean(form.is_active && editingUser?.user_verified !== false)} disabled={editingUser?.is_delete || editingUser?.user_verified === false} onChange={(event) => updateField("is_active", event.target.checked)} sx={switchSx} />
          {editingUser?.user_verified === false ? "Pending" : "Active"}
        </label>
        <label className={`inline-flex items-center gap-2 text-[var(--color-text)] xl:text-base md:text-sm text-xs font-bold ${editingUser?.is_delete ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}><Switch checked={form.is_staff} disabled={editingUser?.is_delete} onChange={(event) => updateField("is_staff", event.target.checked)} sx={switchSx} />Staff</label>
        <label className={`inline-flex items-center gap-2 text-[var(--color-text)] xl:text-base md:text-sm text-xs font-bold ${editingUser?.is_delete ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}><Switch checked={form.is_superuser} disabled={editingUser?.is_delete} onChange={(event) => updateField("is_superuser", event.target.checked)} sx={switchSx} />Superuser</label>
      </div>
      <button type="submit" disabled={saving} className={`inline-flex items-center justify-center justify-self-start gap-2 min-h-[42px] px-3 py-2.5 border border-[var(--color-primary)] rounded-[var(--radius-md)] bg-[var(--color-primary)] text-black md:text-sm text-xs font-black ${saving ? "cursor-not-allowed" : "cursor-pointer"}`}>
        {editingUser ? <SaveOutlined sx={{ fontSize: 18 }} /> : <AddOutlined sx={{ fontSize: 18 }} />}
        {saving ? "Saving..." : editingUser ? "Save user" : "Add user"}
      </button>
    </form>
  );
}

function getInitial(name = "") {
  return (name.trim()[0] || "U").toUpperCase();
}

function UserDetailModal({ user, onClose, onToggleActive, onDelete, busyUserId }) {
  const labelClass = "md:text-sm text-xs label-caps block m-0";
  const valueClass = "m-0 text-[var(--color-text)] xl:text-lg md:text-base text-sm font-bold";

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && user && onClose) {
        onClose();
      }
    };

    if (user) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [user, onClose]);

  if (!user) return null;

  const role = user.is_superuser ? "Superuser" : user.is_staff ? "Staff" : "Regular User";
  
  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 backdrop-blur-[16px]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[20px] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 pb-4">
          <h3 className="m-0 text-[var(--color-text)] xl:text-2xl md:text-xl text-lg font-bold">
            User <span className="text-[var(--color-primary)]">Details</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text)] bg-transparent border-none cursor-pointer hover:bg-[rgba(255,255,255,0.05)] transition-colors"
          >
            <CloseOutlined sx={{ fontSize: 22 }} />
          </button>
        </div>
        
        <div className="flex flex-col gap-6 p-6 pt-0">
          <div className="flex items-center gap-4">
            <div className="w-[52px] h-[52px] rounded-[16px] flex items-center justify-center border border-[var(--color-primary)] text-[var(--color-primary)] text-xl font-bold bg-transparent">
              {getInitial(user.first_name || user.email)}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[var(--color-text)] font-bold md:text-lg text-base truncate">
                {user.first_name} {user.last_name}
              </span>
              <span className="text-[var(--color-text)] md:text-sm text-xs truncate">
                {user.email}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <p className={labelClass}>ROLE</p>
              <p className={valueClass}>{role}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <p className={labelClass}>DATE JOINED</p>
              <p className={valueClass}>{formatDateOnly(user.date_joined || user.created_at, "Never")}</p>
            </div>
          </div>
          
          <div className="flex flex-col gap-1.5">
            <p className={labelClass}>LAST LOGIN</p>
            <p className={valueClass}>{formatDateTime(user.last_login, "Never")}</p>
          </div>

          <hr className="border-t border-[var(--color-border)] my-0" />

          <div className={`flex items-center justify-between gap-4 ${user.is_delete || user.user_verified === false ? "opacity-50 cursor-not-allowed" : ""}`}>
            <div className="flex flex-col gap-1.5">
              <p className={labelClass}>ACTIVE STATUS</p>
              <p className="m-0 text-[var(--color-text)] md:text-sm text-xs font-normal">Enable or disable user login</p>
            </div>
            <div className={user.is_delete || user.user_verified === false ? "pointer-events-none" : ""}>
              <Switch
                checked={Boolean(user.is_active && user.user_verified !== false)}
                disabled={busyUserId === user.id || user.is_delete || user.user_verified === false}
                onChange={() => onToggleActive(user, "is_active")}
                sx={switchSx}
              />
            </div>
          </div>

          <div className="flex items-center gap-4 mt-2">
            <button
              type="button"
              onClick={() => {
                onClose();
                onDelete(user);
              }}
              disabled={busyUserId === user.id || user.is_delete}
              className={`flex-1 min-h-[46px] px-4 border border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)] rounded-[var(--radius-md)] font-bold md:text-sm text-xs uppercase tracking-[0.04em] ${(busyUserId === user.id || user.is_delete) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              DELETE USER
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersModule() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_USER_FORM);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const formRef = useRef(null);
  const firstInputRef = useRef(null);
  const sentinelRef = useRef(null);

  const filteredUsers = users;

  const fetchUsers = useCallback(async (search = "", pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    setErrorMessage("");
    try {
      const data = await getAdminUsers(search, pageNum, 15);
      const results = Array.isArray(data) ? data : (data.results || []);
      const count = Array.isArray(data) ? results.length : (data.count || 0);

      setUsers((prev) => {
        const newUsers = append ? [...prev, ...results] : results;
        setHasMore(newUsers.length < count);
        return sortUsersByDate(newUsers);
      });
      setPage(pageNum);
    } catch (error) {
      setErrorMessage(getPlanErrorMessage(error, "Unable to load users."));
    } finally { 
      setLoading(false); 
      setLoadingMore(false);
    }
  }, []);

  // Auto-search: fire 400 ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(searchTerm.trim(), 1, false);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchUsers]);

  // Infinite scroll: auto-load more when sentinel becomes visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchUsers(searchTerm.trim(), page + 1, true);
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, page, fetchUsers, searchTerm]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const resetForm = () => { setForm(EMPTY_USER_FORM); setEditingUser(null); };

  const startEdit = (user) => {
    setEditingUser(user);
    setForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      password: "",
      user_verified: user.user_verified ?? true,
      is_active: user.is_active ?? true,
      is_staff: user.is_staff ?? false,
      is_superuser: user.is_superuser ?? false,
    });
    setErrorMessage(""); setSuccessMessage("");
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
      document.body.scrollTo({ top: 0, behavior: "smooth" });
      const mainContent = document.querySelector("main") || document.querySelector(".dashboard-main-content") || document.querySelector(".admin-content-layout");
      if (mainContent) {
        mainContent.scrollTo({ top: 0, behavior: "smooth" });
      }
      firstInputRef.current?.focus({ preventScroll: true });
    }, 100);
  };

  const buildPayload = () => {
    const payload = { ...form, email: form.email.trim(), first_name: form.first_name.trim(), last_name: form.last_name.trim() };
    if (editingUser && !payload.password) delete payload.password;
    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault(); setSaving(true); setErrorMessage(""); setSuccessMessage("");
    try {
      if (editingUser) {
        const updatedUser = await updateAdminUser(editingUser.id, buildPayload());
        setUsers((current) => sortUsersByDate(current.map((item) => (item.id === updatedUser.id ? updatedUser : item))));
        setSuccessMessage("User updated.");
      } else {
        const savedUser = await createAdminUser(buildPayload());
        setUsers((current) => sortUsersByDate([savedUser, ...current]));
        setSuccessMessage("User added.");
      }
      resetForm();
    } catch (error) {
      setErrorMessage(getPlanErrorMessage(error, "Unable to save user."));
    } finally { setSaving(false); }
  };

  const toggleUserField = async (user, field) => {
    setBusyUserId(user.id); setErrorMessage(""); setSuccessMessage("");
    try {
      const updatedUser = await updateAdminUser(user.id, { [field]: !user[field] });
      setUsers((current) => current.map((item) => (item.id === updatedUser.id ? updatedUser : item)));
      setSelectedUser((current) => current && current.id === updatedUser.id ? updatedUser : current);
      setSuccessMessage("User updated.");
    } catch (error) {
      setErrorMessage(getPlanErrorMessage(error, "Unable to update user."));
    } finally { setBusyUserId(null); }
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(`Delete ${user.email}?`);
    if (!confirmed) return;
    setBusyUserId(user.id); setErrorMessage(""); setSuccessMessage("");
    try {
      await deleteAdminUser(user.id);
      setUsers((current) => current.filter((item) => item.id !== user.id));
      setSuccessMessage("User deleted.");
    } catch (error) {
      setErrorMessage(getPlanErrorMessage(error, "Unable to delete user."));
    } finally { setBusyUserId(null); }
  };

  return (
    <div className="grid gap-4">
      <UserForm form={form} setForm={setForm} editingUser={editingUser} saving={saving} onSubmit={handleSubmit} onCancel={resetForm} formRef={formRef} firstInputRef={firstInputRef} />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-[1_1_260px]">
          <SearchOutlined sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 18, color: "var(--color-text-muted)" }} />
          <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search users" className="w-full py-3 pr-4 pl-9 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] text-[var(--color-text)] xl:text-base md:text-sm text-xs font-black outline-none" />
        </div>
      </div>

      {(errorMessage || successMessage) && (
        <div className={`px-4 py-3.5 border rounded-[var(--radius-md)] md:text-sm text-xs ${errorMessage ? "border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] text-[var(--color-accent-red)]" : "border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)]"}`}>
          {errorMessage || successMessage}
        </div>
      )}

      <div className="border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] overflow-hidden">
        {loading ? (
          <div className="grid gap-px">{[1, 2, 3].map((item) => <div key={item} className="skeleton h-[70px] bg-[rgba(255,255,255,0.04)]" />)}</div>
        ) : filteredUsers.length === 0 ? (
          <div className="min-h-[180px] grid place-items-center xl:text-xl md:text-lg text-base text-[var(--color-text-muted)] text-center">No users found.</div>
        ) : (
          <div>
            {filteredUsers.map((user) => {
              return (
                <div key={user.id} className="admin-user-row grid xl:grid-cols-[minmax(190px,1fr)_minmax(142px,0.55fr)_max-content_92px] gap-4 items-center min-h-[88px] p-4 first:border-t-0 border-t border-[var(--color-border)]" onClick={() => setSelectedUser(user)} style={{ cursor: "pointer" }}>
                  <div className="admin-user-identity min-w-0 flex flex-col gap-1">
                    <h5 className="m-0 text-[var(--color-text)] xl:text-lg md:text-base text-sm font-black">{user.first_name} {user.last_name}</h5>
                    <p className="m-0 text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs leading-[1.5] [overflow-wrap:anywhere]">{user.email}</p>
                  </div>
                  <div className="admin-user-status flex flex-col md:items-start items-end gap-1">
                    <UserStatusPill active={user.is_active} isDelete={user.is_delete} userVerified={user.user_verified} />
                    <p className="text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs">{user.user_verified ? "Email verified" : "Email unverified"} | {user.is_superuser ? "Superuser" : user.is_staff ? "Staff" : "User"}</p>
                  </div>
                  <div className="admin-user-active-control inline-flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <label className={`inline-flex items-center gap-1 text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs font-extrabold ${(busyUserId === user.id || user.user_verified === false || user.is_delete) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                      {user.user_verified === false ? "Pending" : user.is_active ? "Active" : "Inactive"}
                      <Switch checked={Boolean(user.is_active && user.user_verified !== false)} disabled={busyUserId === user.id || user.user_verified === false || user.is_delete} onChange={() => toggleUserField(user, "is_active")} sx={switchSx} />
                    </label>
                  </div>
                  <div className="admin-user-actions inline-flex gap-2 justify-end">
                    <button type="button" onClick={(e) => { e.stopPropagation(); startEdit(user); }} disabled={busyUserId === user.id} aria-label={`Edit ${user.email}`} title="Edit" className={`w-10 h-10 border border-[var(--color-border)] bg-transparent rounded-[var(--radius-md)] text-[var(--color-text)] ${busyUserId === user.id ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <EditOutlined sx={{ fontSize: 18 }} />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(user); }} disabled={busyUserId === user.id || user.is_delete} aria-label={`Delete ${user.email}`} title="Delete" className={`w-10 h-10 border border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-accent-red)] ${(busyUserId === user.id || user.is_delete) ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                      <DeleteOutlineOutlined sx={{ fontSize: 18 }} />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Sentinel for infinite scroll + loading indicator */}
            {hasMore && (
              <div ref={sentinelRef} className="p-4 flex items-center justify-center gap-2 border-t border-[var(--color-border)]">
                {loadingMore ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                    <span className="md:text-sm text-xs font-bold text-[var(--color-text)]">Loading more...</span>
                  </>
                ) : (
                  <span className="md:text-sm text-xs font-bold text-[var(--color-text)]">Scroll for more</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <UserDetailModal 
        user={selectedUser} 
        onClose={() => setSelectedUser(null)} 
        onToggleActive={toggleUserField} 
        onDelete={handleDelete} 
        busyUserId={busyUserId} 
      />
    </div>
  );
}

export default UsersModule;
